import { ActionCommand, User } from '@nextforce/api'
import { ListItem, Text, makeStyles, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import React, { useState } from 'react'
import { FlatList, Pressable } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { CommandButton } from 'shared/components/command_button'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { RoundedButton } from 'shared/components/rounded-button'
import { EmptyUserListView } from 'shared/components/user/empty_user_list'
import { UserAvatar } from 'shared/components/user/user_avatar'
import { CrewMetrics } from 'users/anytime_metrics/views'

interface Props {
  screenType: string | undefined
  users: User[] | undefined
  onPressUser: (user: User) => void
  isSingleSelectList?: boolean
  onPerformSwapCover: (user: User) => Promise<void>
  command?: ActionCommand
  noUsersNote?: string
  cancelHistory?: boolean
  isAddAndAssign: boolean
  setSelectedCrew: React.Dispatch<React.SetStateAction<User | undefined>>
  onAssignToCrew(): void
  onPressAnytimeMetrics(): void
}

export function UserListView({
  screenType,
  users,
  onPressUser,
  isSingleSelectList,
  onPerformSwapCover,
  command,
  noUsersNote,
  cancelHistory,
  isAddAndAssign,
  setSelectedCrew,
  onAssignToCrew,
  onPressAnytimeMetrics,
}: Props) {
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined)
  const styles = useStyles()
  const { theme } = useTheme()

  function onSelectUser(user: User) {
    setSelectedUser(user)
    if (!isSingleSelectList) {
      onPressUser(user)
    }
  }

  if (!users) {
    return null
  }

  if (users.length === 0) {
    return <EmptyUserListView noUsersNote={noUsersNote} />
  }

  function HeaderComponent() {
    return screenType === 'recent' ? (
      <PressableOpacity
        onPress={onPressAnytimeMetrics}
        style={[styles.listItem, { backgroundColor: theme.colors.white }]}
      >
        <Text style={{ color: theme.colors.grey2 }}>
          {t('crewMetricsLearnMore')}
        </Text>
      </PressableOpacity>
    ) : null
  }

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.container}>
      <FlatList
        style={styles.list}
        data={users}
        keyExtractor={item => String(item.id)}
        ListHeaderComponent={<HeaderComponent />}
        renderItem={({ item }) => (
          <UserRow
            cancelHistory={cancelHistory}
            user={item}
            onSelectUser={() => onSelectUser(item)}
            selected={selectedUser && item.id === selectedUser.id}
            isSingleSelectList={isSingleSelectList}
          />
        )}
        onEndReachedThreshold={1}
      />
      {isAddAndAssign ? (
        <RoundedButton
          title={t('assignToThisCrew')}
          onPress={() => {
            setSelectedCrew(selectedUser)
            onAssignToCrew()
          }}
          containerStyle={styles.buttonContainer}
          disabled={!selectedUser}
        />
      ) : isSingleSelectList && command ? (
        <CommandButton
          command={command}
          disabled={!selectedUser}
          onPress={() => onPerformSwapCover(selectedUser!)}
          containerStyle={styles.buttonContainer}
        />
      ) : null}
    </SafeAreaView>
  )
}

interface UserRowProps {
  user: User
  onSelectUser: () => void
  selected?: boolean
  isSingleSelectList?: boolean
  cancelHistory?: boolean
}

function UserRow({
  user,
  onSelectUser,
  selected,
  isSingleSelectList,
  cancelHistory,
}: UserRowProps) {
  const styles = useStyles()
  return (
    <Pressable onPress={onSelectUser}>
      <ListItem containerStyle={styles.listItem}>
        <UserAvatar user={user} size="medium" />
        <ListItem.Content>
          <ListItem.Title>{`${user.firstName} ${user.lastName} ${
            user.cancelHistory?.items && user.cancelHistory?.items?.length > 3
              ? '🛑'
              : ''
          }`}</ListItem.Title>
          {cancelHistory && (
            <ListItem.Subtitle style={styles.subtitle}>
              {user.notes}
            </ListItem.Subtitle>
          )}
        </ListItem.Content>
        <CrewMetrics user={user} />
        {isSingleSelectList && (
          <ListItem.CheckBox
            onPress={onSelectUser}
            iconRight
            checkedIcon="dot-circle-o"
            uncheckedIcon="circle-o"
            checked={selected ?? false}
          />
        )}
      </ListItem>
    </Pressable>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
    justifyContent: 'space-between',
    backgroundColor: theme.colors?.grey5,
  },
  list: {
    paddingVertical: 10,
  },
  listItem: {
    paddingVertical: 20,
    borderBottomWidth: 0.6,
    paddingLeft: 16,
    paddingRight: 0,
  },
  buttonContainer: {
    margin: 10,
  },
  subtitle: { color: theme.colors?.grey1 },
}))
